// src/components/crm/OrphanedCustomers.js

import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const OrphanedCustomers = ({ currentUserProfile }) => {
    const [orphanedCustomers, setOrphanedCustomers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [reassignment, setReassignment] = useState({});
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [bulkAssignUser, setBulkAssignUser] = useState('');

    useEffect(() => {
        // --- FIX: UPDATED PERMISSION CHECK ---
        const ALLOWED_ROLES = [
            'Admin', 
            'Manager', 
            'Owner/System Admin', 
            'Owner / System Admin', 
            'Owner', 
            'System Admin'
        ];

        // Check if the user has ANY of the allowed roles
        const hasPermission = currentUserProfile && ALLOWED_ROLES.includes(currentUserProfile.role);

        if (hasPermission) {
            const usersRef = collection(db, 'users');
            const qUsers = query(usersRef, where('organizationId', '==', currentUserProfile.organizationId));
            const unsubUsers = onSnapshot(qUsers, (snapshot) => {
                const usersData = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(user => user.role !== 'Orphan' && user.status !== 'removed' && user.name);
                setAllUsers(usersData);
            }, (err) => {
                console.error("Error fetching users for reassignment:", err);
                setError("Could not load users list.");
            });

            const orphanCustomersRef = collection(db, `organizations/${currentUserProfile.organizationId}/orphanedCustomers`);
            const unsubCustomers = onSnapshot(orphanCustomersRef, (snapshot) => {
                const customersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setOrphanedCustomers(customersData);
                setIsLoading(false);
            }, (err) => {
                console.error("Error fetching orphaned customers:", err);
                setError("Could not load orphaned customers.");
                setIsLoading(false);
            });

            return () => {
                unsubUsers();
                unsubCustomers();
            };
        } else if (currentUserProfile) {
            // If they are logged in but NOT in the allowed list
            setError("You do not have permission to view this page.");
            setIsLoading(false);
        }
    }, [currentUserProfile]);

    const handleReassignmentChange = (customerId, newOwnerId) => {
        setReassignment(prev => ({ ...prev, [customerId]: newOwnerId }));
    };

    const handleReassign = async (customerId) => {
        const newOwnerId = reassignment[customerId];
        if (!newOwnerId) {
            setError("Please select a new owner.");
            return;
        }
        setError('');
        try {
            const reassignCustomer = httpsCallable(functions, 'reassignCustomer');
            await reassignCustomer({ customerId, newOwnerId });
        } catch (err) {
            console.error("Error reassigning customer:", err);
            setError(err.message);
        }
    };

    const handleSelectCustomer = (customerId) => {
        setSelectedCustomers(prev =>
            prev.includes(customerId)
                ? prev.filter(id => id !== customerId)
                : [...prev, customerId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedCustomers(orphanedCustomers.map(c => c.id));
        } else {
            setSelectedCustomers([]);
        }
    };

    const handleBulkReassign = async () => {
        if (!bulkAssignUser || selectedCustomers.length === 0) {
            setError("Please select users to reassign and a new owner.");
            return;
        }
        setError('');
        try {
            const bulkReassign = httpsCallable(functions, 'bulkReassignCustomers');
            await bulkReassign({ customerIds: selectedCustomers, newOwnerId: bulkAssignUser });
            setSelectedCustomers([]);
        } catch (err) {
            setBulkAssignUser('');
            console.error("Error in bulk reassignment:", err);
            setError(err.message);
        }
    };


    if (isLoading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-bold mb-4">Orphaned Customers</h2>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Orphaned Customers</h2>
            <p className="text-sm text-gray-600 mb-4">
                These customers belonged to users who have been removed. Please reassign them to an active user.
            </p>
            {error && <p className="text-red-500 mb-4">{error}</p>}

            {selectedCustomers.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-md mb-4 flex items-center gap-4">
                    <p className="font-semibold text-blue-800">{selectedCustomers.length} customer(s) selected.</p>
                    <select
                        value={bulkAssignUser}
                        onChange={(e) => setBulkAssignUser(e.target.value)}
                        className="text-sm border rounded px-2 py-1 bg-white"
                    >
                        <option value="">-- Select User --</option>
                        {allUsers.map(user => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                    </select>
                    <button
                        onClick={handleBulkReassign}
                        disabled={!bulkAssignUser}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        Assign Selected
                    </button>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase bg-gray-50">
                        <tr>
                            <th className="px-2 py-3">
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={orphanedCustomers.length > 0 && selectedCustomers.length === orphanedCustomers.length}
                                />
                            </th>
                            <th className="px-6 py-3">Customer Name</th>
                            <th className="px-6 py-3">Previous Owner</th>
                            <th className="px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orphanedCustomers.map(customer => (
                            <tr key={customer.id} className={`border-b ${selectedCustomers.includes(customer.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                <td className="px-2 py-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedCustomers.includes(customer.id)}
                                        onChange={() => handleSelectCustomer(customer.id)}
                                    />
                                </td>
                                <td className="px-6 py-4 font-medium">{customer.name}</td>
                                <td className="px-6 py-4 text-gray-500">{customer.previousOwnerName || 'Unknown'}</td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={reassignment[customer.id] || ''}
                                            onChange={(e) => handleReassignmentChange(customer.id, e.target.value)}
                                            className="text-sm border rounded px-2 py-1 bg-white"
                                        >
                                            <option value="">-- Select User --</option>
                                            {allUsers.map(user => (
                                                <option key={user.id} value={user.id}>{user.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => handleReassign(customer.id)}
                                            disabled={!reassignment[customer.id]}
                                            className="px-3 py-1 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                                        >
                                            Assign
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {orphanedCustomers.length === 0 && (
                    <p className="text-center py-8 text-gray-500">No orphaned customers to display.</p>
                )}
            </div>
        </div>
    );
};

export default OrphanedCustomers;